import React, { useState, useEffect } from 'react';
import { getSkus, createSku, updateSku, deleteSku, getActiveCategories, getBrands, getActiveChains, getSkuPrices, createSkuPrice } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Search, Package, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function SkusPage() {
  const [skus, setSkus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pricesDialogOpen, setPricesDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [skuPrices, setSkuPrices] = useState([]);
  const [newPrice, setNewPrice] = useState({ chain_id: '', price: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category_id: '',
    barcode: '',
    unit: 'unidad',
    is_own_brand: false
  });

  useEffect(() => {
    loadSkus();
    loadFilters();
  }, [search, filterCategory, filterBrand]);

  const loadSkus = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory && filterCategory !== 'all') params.category_id = filterCategory;
      if (filterBrand && filterBrand !== 'all') params.brand = filterBrand;
      
      const response = await getSkus(params);
      setSkus(response.data);
    } catch (error) {
      toast.error('Error cargando SKUs');
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const [catRes, brandRes, chainsRes] = await Promise.all([
        getActiveCategories(),
        getBrands(),
        getActiveChains()
      ]);
      setCategories(catRes.data);
      setBrands(brandRes.data);
      setChains(chainsRes.data);
    } catch (error) {
      toast.error('Error cargando filtros');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id) {
      toast.error('Debes seleccionar una categoría');
      return;
    }
    try {
      if (editing) {
        await updateSku(editing.id, formData);
        toast.success('SKU actualizado');
      } else {
        await createSku(formData);
        toast.success('SKU creado');
      }
      setDialogOpen(false);
      resetForm();
      loadSkus();
      loadFilters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando SKU');
    }
  };

  const handleEdit = (sku) => {
    setEditing(sku);
    setFormData({
      name: sku.name,
      brand: sku.brand,
      category_id: sku.category_id || '',
      barcode: sku.barcode || '',
      unit: sku.unit || 'unidad',
      is_own_brand: !!sku.is_own_brand
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este SKU?')) return;
    try {
      await deleteSku(id);
      toast.success('SKU desactivado');
      loadSkus();
    } catch (error) {
      toast.error('Error desactivando SKU');
    }
  };

  const openPricesDialog = async (sku) => {
    setSelectedSku(sku);
    try {
      const response = await getSkuPrices(sku.id);
      setSkuPrices(response.data);
    } catch (error) {
      setSkuPrices([]);
    }
    setPricesDialogOpen(true);
  };

  const handleAddPrice = async () => {
    if (!newPrice.chain_id || !newPrice.price) return;
    try {
      await createSkuPrice({
        sku_id: selectedSku.id,
        chain_id: newPrice.chain_id,
        price: parseFloat(newPrice.price)
      });
      toast.success('Precio agregado');
      const response = await getSkuPrices(selectedSku.id);
      setSkuPrices(response.data);
      setNewPrice({ chain_id: '', price: '' });
    } catch (error) {
      toast.error('Error agregando precio');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ name: '', brand: '', category_id: '', barcode: '', unit: 'unidad', is_own_brand: false });
  };

  return (
    <div className="space-y-6" data-testid="skus-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">SKUs</h1>
          <p className="text-slate-500">Gestiona el catálogo de productos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-sku-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo SKU
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar SKU' : 'Nuevo SKU'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="sku-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Marca *</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  required
                  data-testid="sku-brand-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Categoría *</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                  <SelectTrigger data-testid="sku-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name} {cat.section_name && `(${cat.section_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="text-sm text-amber-600">Primero crea categorías en Secciones y Categorías</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Código de Barras</Label>
                  <Input
                    value={formData.barcode}
                    onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                    data-testid="sku-barcode-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad</Label>
                  <Input
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    placeholder="unidad, kg, lt..."
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50">
                <input
                  type="checkbox"
                  id="is_own_brand"
                  checked={formData.is_own_brand}
                  onChange={(e) => setFormData({...formData, is_own_brand: e.target.checked})}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="sku-is-own-brand"
                />
                <Label htmlFor="is_own_brand" className="text-sm cursor-pointer">
                  <span className="font-medium text-emerald-700">Es producto nuestro</span>
                  <span className="text-slate-600 block text-xs">
                    Marca esto si el SKU pertenece a tu marca (no a la competencia).
                    La IA usará esta info para auditar fotos.
                  </span>
                </Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="sku-submit-btn">
                  {editing ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar SKUs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-skus-input"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger data-testid="filter-category">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger data-testid="filter-brand">
                <SelectValue placeholder="Todas las marcas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las marcas</SelectItem>
                {brands.map(brand => (
                  <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Marca</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skus.map((sku) => (
                  <TableRow key={sku.id} data-testid={`sku-row-${sku.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{sku.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{sku.brand}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sku.category_name || '-'}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-500">{sku.barcode || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center">
                        <Badge variant={sku.active ? 'default' : 'secondary'}>
                          {sku.active ? 'Activo' : 'Inactivo'}
                        </Badge>
                        {sku.is_own_brand && (
                          <Badge className="bg-emerald-600 hover:bg-emerald-700" data-testid={`own-brand-badge-${sku.id}`}>
                            Nuestro
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openPricesDialog(sku)}
                        title="Configurar precios por cadena"
                      >
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(sku)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(sku.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {skus.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No hay SKUs registrados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Prices Dialog */}
      <Dialog open={pricesDialogOpen} onOpenChange={setPricesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Precios por Cadena: {selectedSku?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing prices */}
            {skuPrices.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cadena</TableHead>
                    <TableHead className="text-right">PVP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skuPrices.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.chain_name} ({p.chain_code})</TableCell>
                      <TableCell className="text-right font-mono">${p.price.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {/* Add new price */}
            <div className="border-t pt-4">
              <Label className="mb-2 block">Agregar/Actualizar Precio</Label>
              <div className="flex gap-2">
                <Select value={newPrice.chain_id} onValueChange={(v) => setNewPrice({...newPrice, chain_id: v})}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Cadena" />
                  </SelectTrigger>
                  <SelectContent>
                    {chains.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="PVP"
                  className="w-24"
                  value={newPrice.price}
                  onChange={(e) => setNewPrice({...newPrice, price: e.target.value})}
                />
                <Button onClick={handleAddPrice} disabled={!newPrice.chain_id || !newPrice.price}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
